/**
 * This module contains a simplified implementation of a radix trie.
 * Using a trie, we can store routes in a way that is slightly easier
 * to traverse and find matches against.
 *
 * If we were to register the following three routes:
 *   - /
 *   - /steven
 *   - /steven/universe
 *
 * the trie would take the following form:
 *   {
 *     "children": {
 *       "": {
 *         "children": {},
 *         "value": [Route]
 *       },
 *       "steven": {
 *         "children": {
 *           "universe": {
 *             "children": {},
 *             "value": [Route]
 *           }
 *         },
 *         "value": [Route]
 *       },
 *     }
 *   }
 *
 * This module exports a `RouteTrie` factory, which has a single method
 * available, `match`. Calling `match` on the instance will do very
 * simple path normalization and return the given route, should one exist.
 *
 * The current implementation asserts the following invariants:
 *   - Each route declares a path property
 *   - Unique path per route
 *   - At most a single dynamic path for any given node
 */

import { pathFromHref } from './url';

class RouteTrie {
  root: $TSFixMe;
  constructor() {
    this.root = {
      children: {},
    };
  }

  add(path: string, value: object) {
    const segments = path.split('/');

    let node = this.root;

    while (segments.length > 0) {
      const segment = segments.shift()!;

      if (segment.charAt(0) === ':') {
        if (__DEV__ || __TEST__) {
          if (node.params && node.children[segment] === undefined) {
            throw new Error(
              'Invariant Violation: A dynamic path is already registered ' +
                'at this node.',
            );
          }
        }

        node.params = true;
      }

      if (node.children[segment] === undefined) {
        node.children[segment] = {
          children: {},
        };
      }

      node = node.children[segment];
    }

    if (__DEV__ || __TEST__) {
      if (node.value) {
        throw new Error(
          `Invariant Violation: Route "${path || '/'}"
          was registered more than once.`,
        );
      }
    }

    node.value = value;
  }

  match(href: string) {
    const segments = href.split('/');
    const params = {};

    let node = this.root;
    while (segments.length > 0 && node !== undefined) {
      const segment = segments.shift()!;
      // If, for some reason, someone passes a dynamic literal
      // (e.g. `/:foo`) to match against, we bail out.
      if (segment.charAt(0) === ':') {
        node = undefined;
        break;
      }

      if (segment in node.children) {
        node = node.children[segment];
      } else if (node.params) {
        const param = Object.keys(node.children).find((name) => {
          return name.charAt(0) === ':';
        });
        // @ts-expect-error TS(2538): Type 'undefined' cannot be used as an index type.
        node = node.children[param];
        // @ts-expect-error TS(7053): Element implicitly has an 'any' type because expre... Remove this comment to see the full error message
        params[param.substr(1)] = decodeURIComponent(segment);
      } else {
        // If there's no exact match and not a parameterized
        // route at this node, bail out.
        node = undefined;
      }
    }

    if (node === undefined || node.value === undefined) {
      return undefined;
    }

    return {
      ...node.value,
      params,
    };
  }
}

// Do some light sanitization on the path by removing the leading
// and trailing slashes and lowercasing the input.
function normalizePath(path: string) {
  return path.replace(/(^\/|\/$)/g, '').toLowerCase();
}

export default function createRouteTrie(
  routes: { path?: string; [key: string]: $TSFixMe }[],
) {
  const trie = new RouteTrie();

  routes.forEach((route: $TSFixMe) => {
    if (__DEV__ || __TEST__) {
      if (route.path === undefined) {
        throw new Error(
          'Invariant Violation: All routes are expected ' +
            'to define the "path" property.',
        );
      }
    }

    trie.add(normalizePath(route.path), route);
  });

  return {
    match: function (href: string) {
      // Handle the development-only edge case of a link containing
      // `/dev.php` by removing it prior to matching.
      if (__DEV__) {
        href = href?.replace(/\/dev.php/, '');
      }

      const path = pathFromHref(href);
      return trie.match(normalizePath(path));
    },

    nearest: function (href: string) {
      const path = normalizePath(pathFromHref(href));
      const paths = path.split('/');
      let match;

      do {
        paths.pop();
        match = trie.match(paths.join('/'));
      } while (paths.length > 0 && match === undefined);

      return match;
    },
  };
}
